import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Package, Plus, Search, Truck, X, Phone, Mail, MapPin, Calendar,
  CheckCircle, AlertCircle, Edit2, RefreshCw,
  Building2, Eye, Pause, Play, User as UserIcon, Clock,
  Pill, Wrench, FlaskConical, Monitor, Armchair, FileEdit,
  MoreHorizontal
} from 'lucide-react';
import { vendorAPI } from '../services/api';
import { useToast } from './UIProvider';
import CountryCodeSelect from './CountryCodeSelect';
import usePhoneCountryCode from '../hooks/usePhoneCountryCode';
import { buildPhone, parsePhone, validatePhone, getPhoneLength } from '../utils/phone';
import { toTitleCase } from '../utils/text';
import DatePicker from './DatePicker';
import Select from './Select';
import { formatClinicDate } from '../utils/clinicTime';
import SortHeader from './SortHeader';
import TablePager, { usePagedRows } from './TablePager';
import useDialog from '../hooks/useDialog';
import { onActivateKey, ROW_FOCUS_RING } from '../utils/keyboard';

// Alphabetical by label; OTHER pinned to the end.
const VENDOR_TYPE_CONFIG = {
  FURNITURE: { label: 'Furniture', icon: Armchair, color: 'from-amber-500 to-orange-600', bgLight: 'bg-amber-50', textColor: 'text-amber-700', borderColor: 'border-amber-200' },
  IT_SUPPLIER: { label: 'IT Supplier', icon: Monitor, color: 'from-cyan-500 to-blue-600', bgLight: 'bg-cyan-50', textColor: 'text-cyan-700', borderColor: 'border-cyan-200' },
  LABORATORY: { label: 'Laboratory', icon: FlaskConical, color: 'from-purple-500 to-violet-600', bgLight: 'bg-purple-50', textColor: 'text-purple-700', borderColor: 'border-purple-200' },
  MAINTENANCE: { label: 'Maintenance', icon: Wrench, color: 'from-orange-500 to-amber-600', bgLight: 'bg-orange-50', textColor: 'text-orange-700', borderColor: 'border-orange-200' },
  MEDICAL_EQUIPMENT: { label: 'Medical Equipment', icon: Building2, color: 'from-blue-500 to-indigo-600', bgLight: 'bg-blue-50', textColor: 'text-blue-700', borderColor: 'border-blue-200' },
  PHARMACEUTICAL: { label: 'Pharmaceutical', icon: Pill, color: 'from-emerald-500 to-teal-600', bgLight: 'bg-emerald-50', textColor: 'text-emerald-700', borderColor: 'border-emerald-200' },
  STATIONERY: { label: 'Stationery', icon: FileEdit, color: 'from-pink-500 to-rose-600', bgLight: 'bg-pink-50', textColor: 'text-pink-700', borderColor: 'border-pink-200' },
  OTHER: { label: 'Other', icon: Package, color: 'from-indigo-500 to-violet-600', bgLight: 'bg-indigo-50', textColor: 'text-indigo-700', borderColor: 'border-indigo-200' }
};

const STATUS_CONFIG = {
  ACTIVE: { label: 'Active', color: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
  PENDING: { label: 'Pending', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  SUSPENDED: { label: 'Suspended', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' }
};

const ActionMenu = ({ vendor, onView, onEdit, onSuspend, onReactivate, openMenuId, setOpenMenuId }) => {
  const menuRef = useRef(null);
  const isOpen = openMenuId === vendor.id;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    // Escape closes the menu and hands focus back to its button.
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      menuRef.current?.querySelector('button')?.focus();
      setOpenMenuId(null);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape, true);
      };
    }
  }, [isOpen, setOpenMenuId]);

  return (
    // Choosing an item parks focus on the menu button first, so focus is not lost with the item.
    <div className="relative" ref={menuRef} onClickCapture={(e) => { const btn = e.currentTarget.querySelector('button'); if (btn && !btn.contains(e.target)) btn.focus(); }}>
      <button
        onClick={() => setOpenMenuId(isOpen ? null : vendor.id)}
        title="More actions"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <MoreHorizontal className="h-5 w-5 text-gray-500" />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-lg border border-gray-200 py-1.5 z-30">
          <button
            onClick={() => { onView(); setOpenMenuId(null); }}
            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Eye className="h-4 w-4 text-gray-400" />
            View Details
          </button>
          {onEdit && (
            <button
              onClick={() => { onEdit(); setOpenMenuId(null); }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Edit2 className="h-4 w-4 text-gray-400" />
              Edit
            </button>
          )}
          {vendor.status === 'ACTIVE' && onSuspend && (
            <button
              onClick={() => { onSuspend(); setOpenMenuId(null); }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <Pause className="h-4 w-4 text-red-400" />
              Suspend
            </button>
          )}
          {vendor.status === 'SUSPENDED' && onReactivate && (
            <button
              onClick={() => { onReactivate(); setOpenMenuId(null); }}
              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-50 transition-colors"
            >
              <Play className="h-4 w-4 text-emerald-400" />
              Reactivate
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const VendorsManagement = ({ hasPermission }) => {
  const showToast = useToast();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('PHARMACY_VENDORS', action) : true),
    [hasPermission],
  );
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [error, setError] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => { loadVendors(); }, []);

  const loadVendors = async () => {
    setLoading(true); setError(null);
    try { setVendors(await vendorAPI.getAll() || []); }
    catch (err) { setError('Failed to load vendors.'); setVendors([]); }
    finally { setLoading(false); }
  };

  // Derive stats from the loaded vendors — single source of truth, can't drift from the filter.
  const stats = React.useMemo(() => ({
    total:     vendors.length,
    active:    vendors.filter(v => v.status === 'ACTIVE').length,
    pending:   vendors.filter(v => v.status === 'PENDING').length,
    suspended: vendors.filter(v => v.status === 'SUSPENDED').length,
  }), [vendors]);

  const filteredVendors = vendors.filter(v => {
    const matchesSearch = !searchTerm || v.vendorName?.toLowerCase().includes(searchTerm.toLowerCase()) || v.vendorCode?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'ALL' || v.vendorType === typeFilter;
    const matchesStatus = statusFilter === 'ALL' || v.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedVendors = [...filteredVendors].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    let aVal, bVal;
    switch (sortConfig.key) {
      case 'name':
        aVal = (a.vendorName || '').toLowerCase();
        bVal = (b.vendorName || '').toLowerCase();
        break;
      case 'type':
        aVal = (VENDOR_TYPE_CONFIG[a.vendorType]?.label || '').toLowerCase();
        bVal = (VENDOR_TYPE_CONFIG[b.vendorType]?.label || '').toLowerCase();
        break;
      case 'status':
        aVal = (a.status || '').toLowerCase();
        bVal = (b.status || '').toLowerCase();
        break;
      case 'city':
        aVal = (a.city || '').toLowerCase();
        bVal = (b.city || '').toLowerCase();
        break;
      default:
        return 0;
    }
    if (aVal < bVal) return -1 * dir;
    if (aVal > bVal) return 1 * dir;
    return 0;
  });

  const hasActiveFilters = searchTerm || typeFilter !== 'ALL' || statusFilter !== 'ALL';
  const paging = usePagedRows(sortedVendors, {
    id: 'vendors',
    resetOn: [sortConfig, searchTerm, typeFilter, statusFilter],
  });

  const clearFilters = () => {
    setSearchTerm('');
    setTypeFilter('ALL');
    setStatusFilter('ALL');
  };

  const handleCreateVendor = async (data) => {
    try { await vendorAPI.create(data); setShowAddModal(false); loadVendors(); showToast('success', 'Vendor added successfully'); }
    catch (err) { showToast('error', 'Failed to add vendor: ' + (err.response?.data?.error || err.message)); }
  };

  const handleUpdateVendor = async (data) => {
    try { await vendorAPI.update(selectedVendor.id, data); setShowEditModal(false); setSelectedVendor(null); loadVendors(); showToast('success', 'Vendor updated'); }
    catch (err) { showToast('error', 'Failed to update vendor: ' + (err.response?.data?.error || err.message)); }
  };

  const handleSuspendVendor = async (reason) => {
    try { await vendorAPI.suspend(selectedVendor.id, reason); setShowSuspendModal(false); setSelectedVendor(null); loadVendors(); showToast('success', 'Vendor suspended'); }
    catch (err) { showToast('error', 'Failed to suspend vendor: ' + (err.response?.data?.error || err.message)); }
  };

  const handleReactivateVendor = async (vendor) => {
    try { await vendorAPI.reactivate(vendor.id); loadVendors(); showToast('success', 'Vendor reactivated'); }
    catch (err) { showToast('error', 'Failed to reactivate vendor: ' + (err.response?.data?.error || err.message)); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center shadow-lg">
                  <Truck className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Vendors Management</h1>
                  <p className="text-gray-500 mt-0.5 text-sm">Manage your clinic's vendors and suppliers</p>
                </div>
              </div>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
              >
                <Plus className="h-5 w-5" />
                Add New Vendor
              </button>
            </div>
          </div>
        </div>

        {/* Stats — click to filter by status. Total clears the filter. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { icon: Package,     label: 'Total Vendors', value: stats.total,     gradient: 'from-blue-500 to-indigo-600',   status: 'ALL' },
            { icon: CheckCircle, label: 'Active',        value: stats.active,    gradient: 'from-emerald-500 to-green-600', status: 'ACTIVE' },
            { icon: Clock,       label: 'Pending',       value: stats.pending,   gradient: 'from-amber-500 to-orange-600',  status: 'PENDING' },
            { icon: Pause,       label: 'Suspended',     value: stats.suspended, gradient: 'from-red-500 to-rose-600',      status: 'SUSPENDED' },
          ].map((s, i) => {
            const isActive = statusFilter === s.status && s.status !== 'ALL';
            const isTotalActive = s.status === 'ALL' && statusFilter === 'ALL';
            const toggle = () => {
              if (s.status === 'ALL') setStatusFilter('ALL');
              else setStatusFilter(isActive ? 'ALL' : s.status);
            };
            return (
              <button
                key={i}
                type="button"
                onClick={toggle}
                title={s.status === 'ALL' ? 'Show all vendors' : isActive ? 'Clear filter' : `Filter by ${s.label}`}
                className={`bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-all text-left ${
                  isActive ? 'ring-2 ring-emerald-500 shadow-lg' : ''
                } ${isTotalActive ? 'ring-2 ring-blue-400' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">{s.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{s.value || 0}</p>
                  </div>
                  <div className={`w-10 h-10 bg-gradient-to-br ${s.gradient} rounded-xl flex items-center justify-center`}>
                    <s.icon className="h-5 w-5 text-white" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-xl p-5 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input type="text" placeholder="Search by name or code..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
            </div>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              options={[{ value: 'ALL', label: 'All Types' }, ...Object.entries(VENDOR_TYPE_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))]}
              className="min-w-[180px]"
              buttonClassName="px-4 py-3 rounded-xl"
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              options={[{ value: 'ALL', label: 'All Status' }, ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))]}
              className="min-w-[140px]"
              buttonClassName="px-4 py-3 rounded-xl"
            />
            <button title="Refresh" onClick={loadVendors} className="p-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors">
              <RefreshCw className={`h-5 w-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Error */}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 flex items-center"><AlertCircle className="h-5 w-5 mr-3" />{error}</div>}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
        ) : filteredVendors.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><Package className="h-10 w-10 text-gray-400" /></div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No vendors found</h3>
            <p className="text-gray-500 mb-6">{searchTerm || statusFilter !== 'ALL' ? 'Try adjusting filters' : 'Add your first vendor'}</p>
            <button onClick={() => setShowAddModal(true)} className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors">Add Vendor</button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl">
              <TablePager paging={paging} noun="vendor" filteredFrom={vendors.length} className="px-4" />
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <SortHeader label="Vendor" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
                    <SortHeader label="Type" sortKey="type" sortConfig={sortConfig} onSort={handleSort} />
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                    <SortHeader label="City" sortKey="city" sortConfig={sortConfig} onSort={handleSort} />
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Delivery</th>
                    <SortHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} />
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paging.pageRows.map(v => {
                    const tc = VENDOR_TYPE_CONFIG[v.vendorType] || VENDOR_TYPE_CONFIG.OTHER;
                    const sc = STATUS_CONFIG[v.status] || STATUS_CONFIG.ACTIVE;
                    return (
                      <tr
                        key={v.id}
                        tabIndex={0}
                        onClick={() => { setSelectedVendor(v); setShowViewModal(true); }}
                        onKeyDown={onActivateKey(() => { setSelectedVendor(v); setShowViewModal(true); })}
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${ROW_FOCUS_RING}`}
                      >
                        <td className="px-4 py-3.5">
                          <div>
                            <p className="font-semibold text-gray-900">{v.vendorName}</p>
                            <p className="text-xs text-gray-400 font-mono">{v.vendorCode}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${tc.bgLight} ${tc.textColor}`}>
                            {tc.label}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="text-sm">
                            {v.contactNumber && (
                              <div className="flex items-center gap-1.5 text-gray-600">
                                <Phone className="h-3.5 w-3.5 text-gray-400" />
                                {v.contactNumber}
                              </div>
                            )}
                            {v.email && (
                              <div className="flex items-center gap-1.5 text-gray-500 mt-0.5">
                                <Mail className="h-3.5 w-3.5 text-gray-400" />
                                <span className="truncate max-w-[180px]">{v.email}</span>
                              </div>
                            )}
                            {!v.contactNumber && !v.email && <span className="text-gray-400">-</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {v.city || <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-600">
                          {v.expectedDeliveryDays ? `${v.expectedDeliveryDays} days` : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${sc.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}></span>
                            {sc.label}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                          <ActionMenu
                            vendor={v}
                            openMenuId={openMenuId}
                            setOpenMenuId={setOpenMenuId}
                            onView={() => { setSelectedVendor(v); setShowViewModal(true); }}
                            onEdit={can('EDIT') ? () => { setSelectedVendor(v); setShowEditModal(true); } : null}
                            onSuspend={can('SUSPEND_VENDOR') ? () => { setSelectedVendor(v); setShowSuspendModal(true); } : null}
                            onReactivate={can('SUSPEND_VENDOR') ? () => handleReactivateVendor(v) : null}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            {hasActiveFilters && (
              <div className="px-4 py-2 border-t border-gray-100 text-sm text-right">
                <button onClick={clearFilters} className="text-emerald-600 hover:text-emerald-700 font-medium hover:underline transition-colors">
                  Clear filters
                </button>
              </div>
            )}
            <TablePager paging={paging} noun="vendor" filteredFrom={vendors.length} position="bottom" className="px-4" />
          </div>
        )}
      </div>

      {showAddModal && <AddVendorModal onClose={() => setShowAddModal(false)} onSubmit={handleCreateVendor} />}
      {showEditModal && selectedVendor && <EditVendorModal vendor={selectedVendor} onClose={() => { setShowEditModal(false); setSelectedVendor(null); }} onSubmit={handleUpdateVendor} />}
      {showViewModal && selectedVendor && <ViewVendorModal vendor={selectedVendor} onClose={() => { setShowViewModal(false); setSelectedVendor(null); }} />}
      {showSuspendModal && selectedVendor && <SuspendVendorModal vendor={selectedVendor} onClose={() => { setShowSuspendModal(false); setSelectedVendor(null); }} onSubmit={handleSuspendVendor} />}
    </div>
  );
};

// ===== How a supplier takes orders =====
// The purchase planner turns "this runs out on Thursday" into "order it on Monday" from these. All
// optional: a supplier without them is treated as taking orders any day, with no minimum.
const WEEKDAYS = [
  { code: 'MON', label: 'Mon' }, { code: 'TUE', label: 'Tue' }, { code: 'WED', label: 'Wed' },
  { code: 'THU', label: 'Thu' }, { code: 'FRI', label: 'Fri' }, { code: 'SAT', label: 'Sat' },
  { code: 'SUN', label: 'Sun' },
];
// Half-hourly through the working day. A list rather than the browser's own time input, which the
// app does not use anywhere.
const CUTOFF_OPTIONS = [
  { value: '', label: 'Any time of day' },
  ...Array.from({ length: 29 }, (_, i) => {
    const mins = 8 * 60 + i * 30;
    const hh = String(Math.floor(mins / 60)).padStart(2, '0');
    const mm = String(mins % 60).padStart(2, '0');
    const h12 = ((Math.floor(mins / 60) + 11) % 12) + 1;
    return { value: `${hh}:${mm}`, label: `${h12}:${mm} ${mins < 720 ? 'AM' : 'PM'}` };
  }),
];
const orderingFormState = (v = {}) => ({
  orderDays: v.orderDays ? v.orderDays.split(',') : [],
  orderCutoffTime: v.orderCutoffTime ? String(v.orderCutoffTime).slice(0, 5) : '',
  minimumOrderValue: v.minimumOrderValue != null ? String(v.minimumOrderValue) : '',
});
const orderingPayload = (f) => ({
  orderDays: f.orderDays.length ? WEEKDAYS.map(d => d.code).filter(c => f.orderDays.includes(c)).join(',') : null,
  orderCutoffTime: f.orderCutoffTime || null,
  minimumOrderValue: f.minimumOrderValue !== '' ? parseFloat(f.minimumOrderValue) : null,
});
export const describeOrderDays = (orderDays) => {
  if (!orderDays) return 'Any day';
  const set = orderDays.split(',');
  return WEEKDAYS.filter(d => set.includes(d.code)).map(d => d.label).join(', ');
};
const cutoffLabel = (t) => CUTOFF_OPTIONS.find(o => o.value === String(t || '').slice(0, 5))?.label || String(t || '').slice(0, 5);

const VendorOrderingFields = ({ formData, handleChange }) => (
  <div className="rounded-xl border border-gray-200 p-4 space-y-3">
    <div>
      <p className="text-sm font-semibold text-gray-700">Takes orders on</p>
      <p className="text-xs text-gray-500">The days their booker takes an order. None chosen means any day, and the planner then assumes one order a week.</p>
      <div className="mt-2 flex gap-2 flex-wrap">
        {WEEKDAYS.map(d => {
          const on = formData.orderDays.includes(d.code);
          return (
            <button
              key={d.code}
              type="button"
              aria-pressed={on}
              onClick={() => handleChange('orderDays', on ? formData.orderDays.filter(c => c !== d.code) : [...formData.orderDays, d.code])}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${on ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
            >
              {d.label}
            </button>
          );
        })}
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Order by</label>
        <Select value={formData.orderCutoffTime} onChange={(v) => handleChange('orderCutoffTime', v)} options={CUTOFF_OPTIONS} buttonClassName="p-2.5 rounded-lg" />
        <p className="mt-1 text-xs text-gray-500">An order after this counts as the next order day's.</p>
      </div>
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Minimum order (Rs)</label>
        <input type="number" min="0" inputMode="decimal" value={formData.minimumOrderValue} onChange={(e) => handleChange('minimumOrderValue', e.target.value)}
          className="w-full p-2.5 border-2 border-gray-200 rounded-lg" placeholder="No minimum" />
      </div>
    </div>
  </div>
);

const AddVendorModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({ vendorName: '', contactNumber: '', email: '', address: '', city: '', vendorType: '', startDate: new Date().toISOString().split('T')[0], referredBy: '', expectedDeliveryDays: '', notes: '', ...orderingFormState() });
  const [errors, setErrors] = useState({});
  const [phoneTouched, setPhoneTouched] = useState(false);
  const phoneCountry = usePhoneCountryCode(formData.contactNumber);

  const handleChange = useCallback((f, v) => { setFormData(p => ({ ...p, [f]: v })); if(errors[f]) setErrors(p => ({ ...p, [f]: null })); }, [errors]);

  const validate = () => {
    const e = {};
    if(!formData.vendorName.trim()) e.vendorName = 'Vendor name is required';
    if(!formData.vendorType) e.vendorType = 'Vendor type is required';
    if(formData.contactNumber && !validatePhone(formData.contactNumber)) e.contactNumber = 'Enter a valid phone number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if(!validate()) return;
    onSubmit({ ...formData, expectedDeliveryDays: formData.expectedDeliveryDays ? parseInt(formData.expectedDeliveryDays) : null, ...orderingPayload(formData) });
  };

  const addVendorDialogRef = useDialog(onClose);

  return (
    <div ref={addVendorDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-6 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center"><Plus className="h-6 w-6 text-white" /></div>
              <div><h2 className="text-xl font-bold text-white">Add New Vendor</h2><p className="text-white/80 text-sm">Enter vendor details</p></div>
            </div>
            <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl"><X className="h-6 w-6 text-white" /></button>
          </div>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Row 1: Name + Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Vendor Name *</label>
              <input type="text" value={formData.vendorName} onChange={(e) => handleChange('vendorName', e.target.value)}
                className={`w-full p-2.5 border-2 rounded-lg ${errors.vendorName ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} placeholder="e.g., ABC Pharma" />
              {errors.vendorName && <p className="text-red-500 text-xs mt-1">{errors.vendorName}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type *</label>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(VENDOR_TYPE_CONFIG).map(([type, config]) => {
                  const Icon = config.icon;
                  const isSelected = formData.vendorType === type;
                  return (
                    <button key={type} type="button" onClick={() => handleChange('vendorType', type)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${isSelected ? `${config.borderColor} ${config.bgLight}` : 'border-gray-200 hover:bg-gray-50'}`}>
                      <Icon className={`h-3.5 w-3.5 ${isSelected ? config.textColor : 'text-gray-400'}`} />
                      <span className={isSelected ? config.textColor : 'text-gray-600'}>{config.label}</span>
                    </button>
                  );
                })}
              </div>
              {errors.vendorType && <p className="text-red-500 text-xs mt-1">{errors.vendorType}</p>}
            </div>
          </div>

          {/* Row 2: Contact + Email + City */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Contact Number</label>
              {(() => {
                const { localDigits } = parsePhone(formData.contactNumber);
                const countryCode = phoneCountry.code;
                const phoneInvalid = phoneTouched && formData.contactNumber && !validatePhone(formData.contactNumber);
                return (
                  <div className={`flex items-stretch border-2 rounded-lg focus-within:ring-2 transition-all ${
                    errors.contactNumber || phoneInvalid
                      ? 'border-red-500 focus-within:ring-red-200 focus-within:border-red-500'
                      : validatePhone(formData.contactNumber)
                        ? 'border-emerald-400 focus-within:ring-emerald-200 focus-within:border-emerald-500'
                        : 'border-gray-200 focus-within:ring-green-200 focus-within:border-green-500'
                  }`}>
                    <CountryCodeSelect
                      value={countryCode}
                      onChange={(newCode) => {
                        setPhoneTouched(true);
                        phoneCountry.pick(newCode);
                        handleChange('contactNumber', buildPhone(newCode, localDigits));
                      }}
                      className="rounded-l-lg"
                    />
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={localDigits}
                      onChange={(e) => {
                        setPhoneTouched(true);
                        const digits = e.target.value.replace(/\D/g, '');
                        const next = buildPhone(countryCode, digits);
                        phoneCountry.typed(next);
                        handleChange('contactNumber', next);
                      }}
                      className="flex-1 px-3 py-2.5 border-0 focus:outline-none focus:ring-0 bg-transparent rounded-r-lg"
                      placeholder={countryCode === '92' ? '3001234567' : 'Phone number'}
                      maxLength={getPhoneLength(countryCode).max}
                    />
                  </div>
                );
              })()}
              {(errors.contactNumber || (phoneTouched && formData.contactNumber && !validatePhone(formData.contactNumber))) && (
                <p className="text-red-500 text-xs mt-1">{errors.contactNumber || 'Enter a valid phone number'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full pl-9 p-2.5 border-2 border-gray-200 rounded-lg" placeholder="vendor@email.com" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">City</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="text" value={formData.city} onChange={(e) => handleChange('city', e.target.value)}
                  onBlur={(e) => handleChange('city', toTitleCase(e.target.value))}
                  className="w-full pl-9 p-2.5 border-2 border-gray-200 rounded-lg" placeholder="Karachi" />
              </div>
            </div>
          </div>

          {/* Row 3: Start Date + Delivery + Referred By */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Start Date</label>
              <DatePicker value={formData.startDate} onChange={(v) => handleChange('startDate', v)} placeholder="Select date" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Delivery (Days)</label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="number" min="1" max="90" value={formData.expectedDeliveryDays} onChange={(e) => handleChange('expectedDeliveryDays', e.target.value)}
                  className="w-full pl-9 p-2.5 border-2 border-gray-200 rounded-lg" placeholder="e.g., 3" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Referred By</label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input type="text" value={formData.referredBy} onChange={(e) => handleChange('referredBy', e.target.value)}
                  onBlur={(e) => handleChange('referredBy', toTitleCase(e.target.value))}
                  className="w-full pl-9 p-2.5 border-2 border-gray-200 rounded-lg" placeholder="Source" />
              </div>
            </div>
          </div>

          <VendorOrderingFields formData={formData} handleChange={handleChange} />

          {/* Row 4: Address + Notes (side by side) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Address</label>
              <input type="text" value={formData.address} onChange={(e) => handleChange('address', e.target.value)}
                onBlur={(e) => handleChange('address', toTitleCase(e.target.value))}
                className="w-full p-2.5 border-2 border-gray-200 rounded-lg" placeholder="Full address" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Notes</label>
              <input type="text" value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)}
                className="w-full p-2.5 border-2 border-gray-200 rounded-lg" placeholder="Additional notes" />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-3xl">
          <button onClick={onClose} className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium">Cancel</button>
          <button onClick={handleSubmit} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium">Add Vendor</button>
        </div>
      </div>
    </div>
  );
};

const EditVendorModal = ({ vendor, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    vendorName: vendor.vendorName||'', contactNumber: vendor.contactNumber||'', email: vendor.email||'',
    address: vendor.address||'', city: vendor.city||'', vendorType: vendor.vendorType||'',
    startDate: vendor.startDate||'', referredBy: vendor.referredBy||'',
    expectedDeliveryDays: vendor.expectedDeliveryDays?.toString()||'', notes: vendor.notes||'',
    ...orderingFormState(vendor)
  });

  const [nameError, setNameError] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(!!vendor.contactNumber);
  const phoneCountry = usePhoneCountryCode(formData.contactNumber);
  const handleChange = useCallback((f, v) => { setFormData(p => ({ ...p, [f]: v })); if (f === 'vendorName') setNameError(''); }, []);

  const handleSubmit = () => {
    if(!formData.vendorName.trim()) { setNameError('Vendor name is required'); return; }
    onSubmit({ ...formData, expectedDeliveryDays: formData.expectedDeliveryDays ? parseInt(formData.expectedDeliveryDays) : null, ...orderingPayload(formData) });
  };

  const editVendorDialogRef = useDialog(onClose);

  return (
    <div ref={editVendorDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-6 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center"><Edit2 className="h-6 w-6 text-white" /></div>
              <div><h2 className="text-xl font-bold text-white">Edit Vendor</h2><p className="text-white/80 text-sm">{vendor.vendorCode}</p></div>
            </div>
            <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl"><X className="h-6 w-6 text-white" /></button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Vendor Name *</label>
              <input type="text" value={formData.vendorName} onChange={e => handleChange('vendorName', e.target.value)} onBlur={e => handleChange('vendorName', toTitleCase(e.target.value))} className={`w-full p-3 border-2 rounded-xl ${nameError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} />
              {nameError && <p className="text-red-500 text-xs mt-1">{nameError}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Contact</label>
              {(() => {
                const { localDigits } = parsePhone(formData.contactNumber);
                const countryCode = phoneCountry.code;
                const phoneInvalid = phoneTouched && formData.contactNumber && !validatePhone(formData.contactNumber);
                return (
                  <div className={`flex items-stretch border-2 rounded-xl focus-within:ring-2 transition-all ${
                    phoneInvalid
                      ? 'border-red-500 focus-within:ring-red-200 focus-within:border-red-500'
                      : validatePhone(formData.contactNumber)
                        ? 'border-emerald-400 focus-within:ring-emerald-200 focus-within:border-emerald-500'
                        : 'border-gray-200 focus-within:ring-green-200 focus-within:border-green-500'
                  }`}>
                    <CountryCodeSelect
                      value={countryCode}
                      onChange={(newCode) => {
                        setPhoneTouched(true);
                        phoneCountry.pick(newCode);
                        handleChange('contactNumber', buildPhone(newCode, localDigits));
                      }}
                      className="rounded-l-xl"
                    />
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={localDigits}
                      onChange={(e) => {
                        setPhoneTouched(true);
                        const digits = e.target.value.replace(/\D/g, '');
                        const next = buildPhone(countryCode, digits);
                        phoneCountry.typed(next);
                        handleChange('contactNumber', next);
                      }}
                      className="flex-1 px-3 py-3 border-0 focus:outline-none focus:ring-0 bg-transparent rounded-r-xl"
                      placeholder={countryCode === '92' ? '3001234567' : 'Phone number'}
                      maxLength={getPhoneLength(countryCode).max}
                    />
                  </div>
                );
              })()}
              {phoneTouched && formData.contactNumber && !validatePhone(formData.contactNumber) && (
                <p className="text-red-500 text-xs mt-1">Enter a valid phone number</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} className="w-full p-3 border-2 border-gray-200 rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">City</label>
              <input type="text" value={formData.city} onChange={e => handleChange('city', e.target.value)} onBlur={e => handleChange('city', toTitleCase(e.target.value))} className="w-full p-3 border-2 border-gray-200 rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
              <Select
                value={formData.vendorType}
                onChange={(v) => handleChange('vendorType', v)}
                options={Object.entries(VENDOR_TYPE_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
                buttonClassName="p-3 border-2 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
              <DatePicker value={formData.startDate} onChange={v => handleChange('startDate', v)} placeholder="Select start date" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Delivery (Days)</label>
              <input type="number" min="1" max="90" value={formData.expectedDeliveryDays} onChange={e => handleChange('expectedDeliveryDays', e.target.value)} className="w-full p-3 border-2 border-gray-200 rounded-xl" placeholder="e.g., 3" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Referred By</label>
              <input type="text" value={formData.referredBy} onChange={e => handleChange('referredBy', e.target.value)} onBlur={e => handleChange('referredBy', toTitleCase(e.target.value))} className="w-full p-3 border-2 border-gray-200 rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
              <input type="text" value={formData.notes} onChange={e => handleChange('notes', e.target.value)} className="w-full p-3 border-2 border-gray-200 rounded-xl" />
            </div>
          </div>
          <VendorOrderingFields formData={formData} handleChange={handleChange} />
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
            <textarea value={formData.address} onChange={e => handleChange('address', e.target.value)} onBlur={e => handleChange('address', toTitleCase(e.target.value))} rows={2} className="w-full p-3 border-2 border-gray-200 rounded-xl resize-none" />
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
          <button onClick={onClose} className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-medium">Cancel</button>
          <button onClick={handleSubmit} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium">Update</button>
        </div>
      </div>
    </div>
  );
};

// VendorRow — icon-led label/value row used inside the categorized cards. Mirrors the
// UserRow / DetailRow pattern from User and Patient View modals so vendors feel like
// they belong to the same family of detail screens.
const VendorRow = ({ icon, label, value, mono = false, highlight = false }) => {
  const isEmpty = value == null || value === '';
  return (
    <div className={`flex items-center justify-between gap-4 p-3.5 rounded-xl transition-all duration-200 hover:shadow-sm ${
      highlight ? 'bg-white bg-opacity-80 shadow-sm' : 'bg-white bg-opacity-40'
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex-shrink-0">{icon}</div>
        <span className="text-sm font-medium text-gray-600 whitespace-nowrap">{label}</span>
      </div>
      <span className={`font-semibold text-sm text-right break-words ${mono ? 'font-mono' : ''} ${
        isEmpty ? 'text-gray-400 italic font-normal' : 'text-gray-900'
      }`}>
        {isEmpty ? 'Not provided' : value}
      </span>
    </div>
  );
};

const ViewVendorModal = ({ vendor, onClose }) => {
  const tc = VENDOR_TYPE_CONFIG[vendor.vendorType] || VENDOR_TYPE_CONFIG.OTHER;
  const sc = STATUS_CONFIG[vendor.status] || STATUS_CONFIG.ACTIVE;
  const TypeIcon = tc.icon;
  const formatDate = (d) => formatClinicDate(d, { day: 'numeric', month: 'long', year: 'numeric' }, null);

  const viewVendorDialogRef = useDialog(onClose, { confirmDiscard: false });

  return (
    <div ref={viewVendorDialogRef} className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">

        {/* Sticky emerald header with dotted pattern — same treatment as the Patient
            and User View modals so the visual language carries across modules. */}
        <div className="flex-shrink-0 relative bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 text-white overflow-hidden">
          <button aria-label="Close" onClick={onClose} className="absolute top-4 right-4 z-10 p-2 hover:bg-white/20 rounded-xl transition-colors">
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="relative p-6">
            <div className="flex items-center space-x-5">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center border-2 border-white border-opacity-30 flex-shrink-0">
                <TypeIcon className="h-8 w-8 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-3xl font-bold text-white">
                  {vendor.vendorName}
                </h2>
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 mt-1.5">
                  <span className="text-sm font-mono text-white text-opacity-90">
                    {vendor.vendorCode}
                  </span>
                  {/* Type chip — vendor's own tone (Pharmaceutical, Equipment, etc.) */}
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-white/40 shadow-sm ${tc.bgLight} ${tc.textColor}`}>
                    {tc.label}
                  </span>
                  {/* Status chip — warm amber for Active, slate for Inactive, rose for
                      Suspended. Same tonal family as the User View modal pills. */}
                  {(() => {
                    const tone = {
                      ACTIVE:    'bg-amber-200 border-amber-300 text-amber-900',
                      INACTIVE:  'bg-slate-200 border-slate-300 text-slate-800',
                      SUSPENDED: 'bg-rose-200 border-rose-300 text-rose-900',
                    }[vendor.status] || 'bg-slate-200 border-slate-300 text-slate-800';
                    const dot = {
                      ACTIVE:    'bg-amber-600',
                      INACTIVE:  'bg-slate-500',
                      SUSPENDED: 'bg-rose-600',
                    }[vendor.status] || 'bg-slate-500';
                    return (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border shadow-sm ${tone}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                        {sc.label}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Suspension banner — only when suspended. Sits at the top so the reason is
              visible the moment the modal opens (same idea as the Patient status banner). */}
          {vendor.status === 'SUSPENDED' && vendor.suspensionReason && (
            <div className="bg-rose-50 border-2 border-rose-200 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-rose-800 mb-1">Suspension Reason</div>
                  <p className="text-sm text-rose-900 whitespace-pre-wrap break-words">{vendor.suspensionReason}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact card */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-11 h-11 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex items-center justify-center">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Contact
                </h3>
              </div>
              <div className="space-y-3">
                <VendorRow icon={<Phone className="h-5 w-5 text-emerald-600" />} label="Phone" value={vendor.contactNumber} />
                <VendorRow icon={<Mail className="h-5 w-5 text-teal-600" />} label="Email" value={vendor.email} />
                <VendorRow icon={<MapPin className="h-5 w-5 text-emerald-600" />} label="City" value={vendor.city} />
                <VendorRow icon={<Building2 className="h-5 w-5 text-teal-600" />} label="Address" value={vendor.address} />
              </div>
            </div>

            {/* Commercial Terms card */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-100 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-11 h-11 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <Truck className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Commercial Terms
                </h3>
              </div>
              <div className="space-y-3">
                <VendorRow
                  icon={<Truck className="h-5 w-5 text-amber-600" />}
                  label="Expected Delivery"
                  value={vendor.expectedDeliveryDays ? `${vendor.expectedDeliveryDays} days` : null}
                  highlight
                />
                <VendorRow icon={<Calendar className="h-5 w-5 text-amber-600" />} label="Takes Orders" value={`${describeOrderDays(vendor.orderDays)}${vendor.orderCutoffTime ? `, by ${cutoffLabel(vendor.orderCutoffTime)}` : ''}`} />
                {vendor.minimumOrderValue != null && (
                  <VendorRow icon={<Truck className="h-5 w-5 text-amber-600" />} label="Minimum Order" value={`Rs. ${Number(vendor.minimumOrderValue).toLocaleString()}`} />
                )}
                <VendorRow icon={<Calendar className="h-5 w-5 text-amber-600" />} label="Active Since" value={formatDate(vendor.startDate)} />
                <VendorRow icon={<UserIcon className="h-5 w-5 text-orange-600" />} label="Referred By" value={vendor.referredBy} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer — right-aligned emerald Close button, matching the other View modals */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const SuspendVendorModal = ({ vendor, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if(!reason.trim()) { setError('Please provide a reason'); return; }
    onSubmit(reason);
  };

  const suspendVendorDialogRef = useDialog(onClose);

  return (
    <div ref={suspendVendorDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-red-500 to-rose-600 p-6 rounded-t-3xl">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center"><AlertCircle className="h-6 w-6 text-white" /></div>
            <div><h2 className="text-xl font-bold text-white">Suspend Vendor</h2><p className="text-white/80 text-sm">{vendor.vendorName}</p></div>
          </div>
        </div>
        <div className="p-6">
          <p className="text-gray-600 mb-4">Please provide a reason for suspending this vendor.</p>
          <textarea value={reason} onChange={(e) => { setReason(e.target.value); if (error) setError(''); }} rows={3}
            className={`w-full p-3 border-2 rounded-xl resize-none ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} placeholder="Reason for suspension..." />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
          <button onClick={onClose} className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-medium">Cancel</button>
          <button onClick={handleSubmit} className="px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl font-medium">Suspend</button>
        </div>
      </div>
    </div>
  );
};

export default VendorsManagement;
